import {createSelector} from '@reduxjs/toolkit';
import {AudioUpload, Conversion, Transcription} from '../../domain/types';
import {PendingTranscription} from '../../lib/pendingTranscriptions';
import {audioDataSelector} from './audio.selectors';
import {transcriptionsDataSelector} from './transcription.selectors';
import {conversionsDataSelector, projectConversionsDataSelector} from './conversions.selectors';
import {pendingByAudioIdSelector} from './pendingTranscriptions.selectors';

/**
 * The workspace is one list of audio files, not three lists the user has to join in their head.
 *
 * Everything downstream of a file — whether it has been transcribed, whether that job is still
 * running, what has been converted out of it — hangs off the file itself, so this selector does the
 * join once and the row components stay dumb.
 *
 * The two links:
 *   audio → transcription   `transcription.audioUploadId`
 *   transcription → conversion  the CSV's R2 key, which the conversion records as `csvPath`.
 *     (`conversion.transcriptionId` is always undefined — the backend does not return it.)
 */
export interface WorkspaceRow {
  audio: AudioUpload;
  /** Newest first. One upload can be transcribed any number of times. */
  transcriptions: Transcription[];
  /** The transcription the row's actions act on: the newest usable one, else the newest at all. */
  activeTranscription: Transcription | null;
  /** Every conversion produced from any of this file's transcriptions, newest first. */
  conversions: Conversion[];
  /**
   * A job the session limit refused, waiting for the budget to reset. Client-side only — there is
   * no server-side row, because the job was never accepted. Null for every other file.
   */
  queued: PendingTranscription | null;
}

const byNewest = <T extends {createdAt: string}>(a: T, b: T) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

/**
 * Conversions land in one of two slice fields depending on which thunk fetched them
 * (`fetchConversions` vs `fetchProjectConversions`), and both can hold rows at once.
 *
 * Reading the "right" one would mean re-deriving which fetch ran, which is exactly the kind of
 * guess that goes stale on a project switch. Merging and de-duplicating by id is always correct,
 * and the row join below filters to the audio actually on screen anyway.
 */
const workspaceConversionsSelector = createSelector(
  [conversionsDataSelector, projectConversionsDataSelector],
  (allConversions, projectConversions) => {
    const byId = new Map<string, Conversion>();
    [...allConversions, ...projectConversions].forEach(conversion => byId.set(conversion.id, conversion));
    return [...byId.values()];
  }
);

/**
 * Transcriptions with no audio file behind them — the older CSV-import flow produced these.
 *
 * They have no row to sit under in an audio-file-shaped list, so the workspace links out to the
 * transcriptions page when any exist rather than pretending they are not there.
 */
export const orphanTranscriptionsSelector = createSelector(
  [transcriptionsDataSelector],
  transcriptions => transcriptions.filter(t => !t.audioUploadId)
);

export const workspaceRowsSelector = createSelector(
  [
    audioDataSelector,
    transcriptionsDataSelector,
    workspaceConversionsSelector,
    pendingByAudioIdSelector,
  ],
  (uploads, transcriptions, conversions, queuedByAudioId): WorkspaceRow[] => {
    const conversionsByCsvKey = new Map<string, Conversion[]>();
    conversions.forEach((conversion) => {
      if (!conversion.csvPath) {
        return;
      }
      const existing = conversionsByCsvKey.get(conversion.csvPath);
      if (existing) {
        existing.push(conversion);
      } else {
        conversionsByCsvKey.set(conversion.csvPath, [conversion]);
      }
    });

    const transcriptionsByAudioId = new Map<string, Transcription[]>();
    transcriptions.forEach((transcription) => {
      if (!transcription.audioUploadId) {
        // A row from the older CSV-import flow: no audio file exists for it to sit under.
        return;
      }
      const existing = transcriptionsByAudioId.get(transcription.audioUploadId);
      if (existing) {
        existing.push(transcription);
      } else {
        transcriptionsByAudioId.set(transcription.audioUploadId, [transcription]);
      }
    });

    return [...uploads].sort(byNewest).map((audio) => {
      const rowTranscriptions = (transcriptionsByAudioId.get(audio.id) ?? []).sort(byNewest);

      // Prefer something the user can act on. A finished job beats a running one, which beats a
      // failed one — otherwise a retry that is still running would hide the CSV they already have.
      const activeTranscription = rowTranscriptions.find(t => t.status === 'completed')
        ?? rowTranscriptions.find(t => t.status === 'processing')
        ?? rowTranscriptions[0]
        ?? null;

      const rowConversions = rowTranscriptions
        .flatMap(t => conversionsByCsvKey.get(t.csvR2Key) ?? [])
        .sort(byNewest);

      return {
        audio,
        transcriptions: rowTranscriptions,
        activeTranscription,
        conversions: rowConversions,
        queued: queuedByAudioId.get(audio.id) ?? null,
      };
    });
  }
);
